import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, TrendingUp, TrendingDown, Minus, Users, UserMinus, ArrowUpRight,
    DollarSign, Target, RefreshCw,
} from "lucide-react";
import MoneyTabs from "@/components/coach/MoneyTabs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const $ = (cents) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

function Delta({ pct, inverse }) {
    if (pct === null || pct === undefined) {
        return <span className="text-zinc-500 text-xs">—</span>;
    }
    if (pct === 0) {
        return <span className="text-zinc-500 text-xs inline-flex items-center gap-1"><Minus className="w-3 h-3" />flat</span>;
    }
    const isUp = pct > 0;
    const good = inverse ? !isUp : isUp;
    const color = good ? "text-emerald-400" : "text-rose-400";
    const Icon = isUp ? TrendingUp : TrendingDown;
    return (
        <span className={`text-xs inline-flex items-center gap-1 ${color}`}>
            <Icon className="w-3 h-3" />
            {Math.abs(pct)}% vs prev wk
        </span>
    );
}

function KpiCard({ icon: Icon, title, value, suffix, prev, delta_pct, inverse, hint, target, numerator, denominator, ribbon }) {
    return (
        <div data-testid={`metric-card-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="relative bg-zinc-950 border border-zinc-800 rounded-2xl p-5">
            {ribbon && (
                <div className="absolute top-0 right-0 px-2 py-0.5 text-[9px] tracking-[0.25em] uppercase bg-brand-red text-white rounded-bl rounded-tr-2xl">
                    {ribbon}
                </div>
            )}
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">{title}</div>
                {Icon && <Icon className="w-4 h-4 text-brand-red" />}
            </div>
            <div className="flex items-baseline gap-1.5">
                <div className="font-display text-4xl tracking-wider text-white">
                    {value === null || value === undefined ? "—" : value}
                </div>
                {suffix && <span className="text-zinc-300 text-sm">{suffix}</span>}
            </div>
            <div className="mt-2 flex items-center gap-2 text-xs text-zinc-400 flex-wrap">
                {(prev !== undefined && prev !== null) && <span>was {prev}{suffix || ""}</span>}
                {(delta_pct !== undefined) && <Delta pct={delta_pct} inverse={inverse} />}
                {target !== null && target !== undefined && (
                    <span className={`text-[10px] tracking-wider uppercase ${value >= target ? "text-emerald-400" : "text-amber-400"}`}>
                        target {target}%
                    </span>
                )}
            </div>
            {denominator !== undefined && denominator !== null && (
                <div className="mt-1 text-[10px] text-zinc-500">
                    {numerator}/{denominator} {hint}
                </div>
            )}
            {hint && denominator === undefined && (
                <div className="mt-1 text-[10px] text-zinc-500">{hint}</div>
            )}
        </div>
    );
}

export default function WeeklyMetrics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = async () => {
        try {
            const r = await axios.get(`${API}/coach/metrics/weekly`, { withCredentials: true });
            setData(r.data);
        } catch (e) {
            toast.error("Couldn't load metrics", { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { load(); }, []);

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    if (!data) return null;
    const { kpis, context, window } = data;
    const target = context.monthly_revenue_target_cents;
    const mrr = context.mrr_cents;
    const pct_to_target = target ? Math.min(100, Math.round((mrr / target) * 100)) : 0;

    return (
        <div data-testid="weekly-metrics-page" className="p-4 md:p-8 max-w-6xl mx-auto space-y-8">
            <MoneyTabs />
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Weekly drumbeat</div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider uppercase mt-1">Metrics</h1>
                    <p className="text-zinc-300 mt-1 text-sm">{window.label} · 5 numbers that move the business</p>
                </div>
                <button
                    type="button"
                    onClick={() => { setRefreshing(true); load(); }}
                    disabled={refreshing}
                    data-testid="metrics-refresh"
                    className="btn-ghost-tactical text-xs disabled:opacity-40 inline-flex items-center gap-2"
                >
                    {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Refresh
                </button>
            </div>

            {/* MRR thermometer — context bar */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-5">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Monthly Recurring Revenue</div>
                        <div className="font-display text-4xl tracking-wider text-white mt-1">{$(mrr)}</div>
                        <div className="text-xs text-zinc-400 mt-1">
                            {context.active_members} active member{context.active_members === 1 ? "" : "s"} ·
                            {" "}{context.active_subscriptions} subscription{context.active_subscriptions === 1 ? "" : "s"}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Target</div>
                        <div className="font-display text-2xl tracking-wider text-zinc-300 mt-1">{$(target)}<span className="text-xs text-zinc-500">/mo</span></div>
                        <div className={`text-[11px] mt-1 ${pct_to_target >= 100 ? "text-emerald-400" : "text-amber-400"}`}>{pct_to_target}% to target</div>
                    </div>
                </div>
                <div className="mt-4 h-2 bg-zinc-900 rounded-full overflow-hidden">
                    <div className="h-full bg-brand-red transition-all duration-500" style={{ width: `${pct_to_target}%` }} />
                </div>
            </div>

            {/* Top 3 KPIs: New / Cancellations / Net */}
            <div>
                <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-400 mb-3">Member flow</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <KpiCard
                        icon={Users}
                        title="New members"
                        value={kpis.new_members.value}
                        prev={kpis.new_members.prev}
                        delta_pct={kpis.new_members.delta_pct}
                    />
                    <KpiCard
                        icon={UserMinus}
                        title="Cancellations"
                        value={kpis.cancellations.value}
                        prev={kpis.cancellations.prev}
                        delta_pct={kpis.cancellations.delta_pct}
                        inverse
                    />
                    <KpiCard
                        icon={ArrowUpRight}
                        title="Net growth"
                        value={kpis.net_growth.value > 0 ? `+${kpis.net_growth.value}` : kpis.net_growth.value}
                        prev={kpis.net_growth.prev > 0 ? `+${kpis.net_growth.prev}` : kpis.net_growth.prev}
                        delta_pct={kpis.net_growth.delta_pct}
                        ribbon={kpis.net_growth.value > 0 ? "growing" : null}
                    />
                </div>
            </div>

            {/* Funnel KPIs */}
            <div>
                <h2 className="text-[11px] tracking-[0.25em] uppercase text-zinc-400 mb-3">Funnel conversion</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <KpiCard
                        icon={Target}
                        title="Asmt → 3-Week"
                        value={kpis.asmt_to_3wk_pct.value === null ? "—" : kpis.asmt_to_3wk_pct.value}
                        suffix={kpis.asmt_to_3wk_pct.value === null ? "" : "%"}
                        target={kpis.asmt_to_3wk_pct.target}
                        numerator={kpis.asmt_to_3wk_pct.numerator}
                        denominator={kpis.asmt_to_3wk_pct.denominator}
                        hint={kpis.asmt_to_3wk_pct.hint}
                    />
                    <KpiCard
                        icon={Target}
                        title="Asmt → 12-Week (direct)"
                        value={kpis.asmt_to_12wk_pct.value === null ? "—" : kpis.asmt_to_12wk_pct.value}
                        suffix={kpis.asmt_to_12wk_pct.value === null ? "" : "%"}
                        numerator={kpis.asmt_to_12wk_pct.numerator}
                        denominator={kpis.asmt_to_12wk_pct.denominator}
                        hint={kpis.asmt_to_12wk_pct.hint}
                    />
                    <KpiCard
                        icon={Target}
                        title="3-Week → 12-Week"
                        value={kpis.threeweek_to_12wk_pct.value === null ? "—" : kpis.threeweek_to_12wk_pct.value}
                        suffix={kpis.threeweek_to_12wk_pct.value === null ? "" : "%"}
                        target={kpis.threeweek_to_12wk_pct.target}
                        numerator={kpis.threeweek_to_12wk_pct.numerator}
                        denominator={kpis.threeweek_to_12wk_pct.denominator}
                        hint={kpis.threeweek_to_12wk_pct.hint}
                    />
                </div>
            </div>

            {/* Plain English notes */}
            <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-5 text-sm text-zinc-300 space-y-2">
                <div className="flex items-center gap-2 text-brand-red text-[10px] tracking-[0.25em] uppercase">
                    <DollarSign className="w-3.5 h-3.5" /> Mental model
                </div>
                <p>You don't grow revenue directly. You grow these inputs:</p>
                <ul className="list-disc list-inside space-y-1 text-zinc-400 ml-2 text-xs">
                    <li><span className="text-white">Net growth &gt; 0 every week</span> = the bucket is filling faster than it drains.</li>
                    <li><span className="text-white">Assessment → 3-Week ≥ 80%</span> = your assessment close works.</li>
                    <li><span className="text-white">3-Week → 12-Week ≥ 70%</span> = your trial-to-commit conversion works.</li>
                    <li>If any number drops, fix it BEFORE spending money on more leads.</li>
                </ul>
            </div>
        </div>
    );
}
